// @flow
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { maxLength, required, wholeNumbersStartingWithOne } from '../../utils/validation';
import React, { Component } from 'react';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import Facet from './Facet';
import { faPlay } from '@fortawesome/pro-solid-svg-icons/faPlay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Form from 'react-bootstrap/lib/Form';
import FormControl from 'react-bootstrap/lib/FormControl';
import FormGroup from 'react-bootstrap/lib/FormGroup';
import InputGroup from 'react-bootstrap/lib/InputGroup';
import isEqual from 'react-fast-compare';
import styles from './RangeFacet.module.scss';

type Props = {
    facetId: string,
    intl: intlShape,
    loadPageWithFacetCollapsed: boolean,
    max: number,
    min: number,
    rangeEntered: Function,
    title: string,
    width: number,
};

type State = {
    inputRequiredIsValid: boolean,
    maxInput: number,
    minInput: number,
    minInputIsValid: boolean,
};

class RangeFacet extends Component<Props, State> {
    state = {
        inputRequiredIsValid: true,
        maxInput: this.props.max,
        minInput: this.props.min,
        minInputIsValid: true,
    };

    shouldComponentUpdate = (nextProps, nextState) =>
        !isEqual(this.props, nextProps) || !isEqual(this.state, nextState);

    onValueChange = event => {
        const newState = {};
        const value = event.target.value;
        const validCharacter = wholeNumbersStartingWithOne(value) || value === '';
        const validLength = maxLength(value, 10);

        if (value === '' || (validCharacter && validLength)) {
            const idArray = event.target.id.split('-');
            const id = idArray[idArray.length - 1];
            newState[id] = event.target.value;
            this.setState(newState);
        }
    };

    validateAtLeastOneValueExists = (min, max) => {
        const valid = required(min) || required(max);
        this.setState({ inputRequiredIsValid: valid });
        return valid;
    };

    validateMinLessThanMaxInput = (min, max) => {
        let valid = true;
        if (required(max) && (required(min) || min === '')) {
            valid = Number(min) <= Number(max);
        }
        this.setState({ minInputIsValid: valid });
        return valid;
    };

    rangeClickHandler = () => {
        const { maxInput, minInput } = this.state;
        const valid =
            this.validateAtLeastOneValueExists(minInput, maxInput) &&
            this.validateMinLessThanMaxInput(minInput, maxInput);

        if (valid) {
            this.props.rangeEntered(this.props.facetId, minInput, maxInput);
        }
    };

    submitHandler = event => {
        this.rangeClickHandler();
        event.preventDefault();
    };

    render() {
        const { loadPageWithFacetCollapsed, title = '', width, intl } = this.props;

        const { inputRequiredIsValid, maxInput, minInput, minInputIsValid } = this.state;

        const { formatMessage } = intl;

        const valid = minInputIsValid && inputRequiredIsValid;
        const validationProps = valid ? {} : { validationState: 'error' };
        let validationFormattedMessageId;
        if (!minInputIsValid) validationFormattedMessageId = 'you_must_enter_a_min_value_less_than_max';
        if (!inputRequiredIsValid) validationFormattedMessageId = 'you_must_enter_at_lease_one';
        const facetClass = title
            .trim()
            .replace('/', '')
            .replace(/\s+/g, '-')
            .toLowerCase();

        return (
            <Facet className={`range-facet ${facetClass}`} title={title} closeFacet={loadPageWithFacetCollapsed}>
                <Form inline onSubmit={this.submitHandler} className={styles['range-facet']}>
                    <FormGroup bsSize="sm" {...validationProps} className={styles.range}>
                        <InputGroup className={styles['range-input']}>
                            <InputGroup.Addon className={styles['money-icon']}>$</InputGroup.Addon>
                            <FormControl
                                className={styles['range-facet-text']}
                                id={`${facetClass}-minInput`}
                                onChange={this.onValueChange}
                                placeholder={formatMessage({ id: 'search_minimum' })}
                                type={width < 500 ? 'number' : 'text'}
                                value={minInput}
                            />
                        </InputGroup>
                        <span className={styles['range-separator']}>&#45;</span>
                        <InputGroup className={styles['range-input']}>
                            <InputGroup.Addon className={styles['money-icon']}>$</InputGroup.Addon>
                            <FormControl
                                className={styles['range-facet-text']}
                                id={`${facetClass}-maxInput`}
                                onChange={this.onValueChange}
                                placeholder={formatMessage({ id: 'search_maximum' })}
                                type={width < 500 ? 'number' : 'text'}
                                value={maxInput}
                            />
                        </InputGroup>
                        <Button
                            className={styles['range-facet-button']}
                            onClick={this.rangeClickHandler}
                            primary
                            small
                            type="submit"
                        >
                            <FontAwesomeIcon icon={faPlay} className={styles['range-facet-button-icon']} />
                        </Button>
                        {!valid && (
                            <div className={styles.response}>
                                <span className={styles.error}>
                                    <FormattedMessage id={validationFormattedMessageId} />
                                </span>
                            </div>
                        )}
                    </FormGroup>
                </Form>
            </Facet>
        );
    }
}

export default injectIntl(RangeFacet);
